import { Component, input, effect, inject, signal, untracked } from '@angular/core';
import { InventoryService } from '@servicios/inventory/inventory.service';
import { AuthenticationService } from '@servicios/authentication.service';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Router } from '@angular/router';
import { StockTransferFormComponent } from '../stock-transfer-form/stock-transfer-form.component';

import { BranchService } from '@servicios/branch.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-table-stock-transfers',
  templateUrl: './table-stock-transfers.component.html',
  styleUrls: ['./table-stock-transfers.component.scss'],
  standalone: false,
  providers: [DialogService]
})
export class TableStockTransfersComponent {
  private service = inject(InventoryService);
  private branchService = inject(BranchService);
  private messageService = inject(MessageService);
  private authService = inject(AuthenticationService);
  private router = inject(Router);

  loading = signal<boolean>(false);
  data = signal<any[]>([]);

  refreshTrigger = input<number>(0);
  branchId = input<string | null>(null);
  
  userBranches = signal<{id: string, name: string}[]>([]);

  // Filtros
  statusFilter = signal<string | null>(null);
  directionFilter = signal<'all' | 'sent' | 'received'>('all');

  statusOptions = [
    { label: 'Todos', value: null },
    { label: 'Pendiente', value: 'PENDING' },
    { label: 'En Tránsito', value: 'IN_TRANSIT' },
    { label: 'Completado', value: 'COMPLETED' },
    { label: 'Rechazado', value: 'REJECTED' }
  ];

  directionOptions = [
    { label: 'Todas', value: 'all' },
    { label: 'Emitidas (Origen)', value: 'sent' },
    { label: 'Recibidas (Destino)', value: 'received' }
  ];

  constructor() {
    this.loadBranches();

    effect(() => {
      const trigger = this.refreshTrigger();
      const branchId = this.branchId();
      const status = this.statusFilter();
      const direction = this.directionFilter();
      untracked(() => {
        this.loadData();
      });
    });
  }

  async loadBranches() {
    try {
      const res = await firstValueFrom(this.branchService._getAll());
      this.userBranches.set(res.data?.branches || []);
    } catch (e) {
      console.error(e);
    }
  }

  getBranchName(id: string) {
    const b = this.userBranches().find(b => b.id === id);
    return b ? b.name : id;
  }

  async loadData() {
    this.loading.set(true);
    try {
      let filters: any = {};
      if (this.statusFilter()) {
        filters.status = this.statusFilter();
      }
      
      const bid = this.branchId();
      if (bid) {
        if (this.directionFilter() === 'sent') {
          filters.sourceBranchId = bid;
        } else if (this.directionFilter() === 'received') {
          filters.targetBranchId = bid;
        } else {
          // If all, we can't easily filter by "source OR target" in Prisma via simple query params unless backend supports it.
          // Assuming backend findAll without branch filter returns all, and we'll filter locally if needed, 
          // or we pass branchId and backend checks source OR target.
          // Wait, backend supports `sourceBranchId` and `targetBranchId`.
          // We will fetch all and filter locally for 'all' if a branch is selected to avoid complex backend changes right now.
          // Actually, if branchId is selected, let's just fetch all and filter in frontend.
        }
      }

      const res = await this.service.getAll('stock-transfers', { filters });
      
      let filteredData = res.data || res;
      if (Array.isArray(filteredData)) {
        if (bid) {
          if (this.directionFilter() === 'all') {
            filteredData = filteredData.filter((t: any) => t.sourceBranchId === bid || t.targetBranchId === bid);
          } else if (this.directionFilter() === 'sent') {
            filteredData = filteredData.filter((t: any) => t.sourceBranchId === bid);
          } else if (this.directionFilter() === 'received') {
            filteredData = filteredData.filter((t: any) => t.targetBranchId === bid);
          }
        }
        this.data.set(filteredData);
      }
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las transferencias' });
    } finally {
      this.loading.set(false);
    }
  }

  getStatusName(status: string) {
    const opt = this.statusOptions.find(o => o.value === status);
    return opt ? opt.label : status;
  }

  getSeverity(status: string) {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'IN_TRANSIT': return 'info';
      case 'COMPLETED': return 'success';
      case 'REJECTED': return 'danger';
      default: return 'secondary';
    }
  }

  openTransferForm(mode: 'create' | 'approve' | 'receive' | 'view', transferId?: string) {
    if (mode === 'create') {
      this.router.navigate(['/inventory/transfers/new'], { queryParams: { mode } });
    } else {
      this.router.navigate(['/inventory/transfers', transferId], { queryParams: { mode } });
    }
  }
}
